class PDPMainProduct {
  constructor(sectionId, options) {
    this.sectionId = sectionId;
    this.options = options || {};
    this.root = document.getElementById(`pdp-hero-${sectionId}`);
    if (!this.root) return;

    const variantsScript = document.getElementById(`pdp-variants-${sectionId}`);
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    this.selectedOptions = {};
    this.currentVariant = this.variants.find((v) => v.id === this.options.currentVariantId) || this.variants[0];

    if (this.currentVariant) {
      this.currentVariant.options.forEach((val, i) => {
        this.selectedOptions[i] = val;
      });
    }

    this.thumbTrack = this.root.querySelector('[data-thumb-track]');
    this.mainTrack = this.root.querySelector('[data-main-track]');
    this.discountBadge = this.root.querySelector('[data-discount-badge]');
    this.priceCurrent = this.root.querySelector('[data-price-current]');
    this.priceCompare = this.root.querySelector('[data-price-compare]');
    this.stockEl = this.root.querySelector('[data-pdp-stock]');
    this.skuEl = this.root.querySelector('[data-pdp-sku]');
    this.addToCartBtn = this.root.querySelector('[data-add-to-cart]');
    this.buyNowBtn = this.root.querySelector('[data-buy-now]');
    this.qtyInput = this.root.querySelector('[data-qty-input]');

    this.bindGallery();
    this.bindOptionSelectors();
    this.bindQuantity();
    this.bindCartActions();
    this.bindShare();
    this.bindStickyBar();
  }

  // ---------- Gallery ----------
  bindGallery() {
    const thumbs = Array.from(this.root.querySelectorAll('.pdp-gallery__thumb'));
    const slides = Array.from(this.root.querySelectorAll('.pdp-gallery__main-slide'));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = thumb.getAttribute('data-thumb-index');
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
        slides.forEach((s) => {
          s.classList.toggle('is-active', s.getAttribute('data-main-slide-index') === index);
        });
      });
    });

    const upBtn = this.root.querySelector('[data-thumbs-up]');
    const downBtn = this.root.querySelector('[data-thumbs-down]');
    if (upBtn && this.thumbTrack) {
      upBtn.addEventListener('click', () => this.thumbTrack.scrollBy({ top: -80, behavior: 'smooth' }));
    }
    if (downBtn && this.thumbTrack) {
      downBtn.addEventListener('click', () => this.thumbTrack.scrollBy({ top: 80, behavior: 'smooth' }));
    }

    this.bindSwipe(slides, thumbs);
  }

  bindSwipe(slides, thumbs) {
    if (!this.mainTrack || slides.length <= 1) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let isSwiping = false;

    const getActiveIndex = () =>
      slides.findIndex((s) => s.classList.contains('is-active'));

    const goToIndex = (index) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((s, i) => s.classList.toggle('is-active', i === clamped));
      thumbs.forEach((t) => {
        t.classList.toggle('is-active', parseInt(t.getAttribute('data-thumb-index'), 10) === clamped);
      });
      const activeThumb = thumbs[clamped];
      if (activeThumb && this.thumbTrack) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    };

    this.mainTrack.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = true;
    }, { passive: true });

    this.mainTrack.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;
      const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
      // If the gesture is more horizontal than vertical, treat as a swipe (prevent page scroll)
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }, { passive: false });

    this.mainTrack.addEventListener('touchend', (e) => {
      if (!isSwiping) return;
      isSwiping = false;
      const touchEndX = e.changedTouches[0].clientX;
      const deltaX = touchEndX - touchStartX;
      const threshold = 40;

      if (Math.abs(deltaX) > threshold) {
        const current = getActiveIndex();
        if (deltaX < 0) {
          goToIndex(current + 1); // swiped left → next
        } else {
          goToIndex(current - 1); // swiped right → previous
        }
      }
    });
  }

  showMediaById(mediaId) {
    if (!mediaId) return;
    const slide = this.root.querySelector(`.pdp-gallery__main-slide[data-media-id="${mediaId}"]`);
    const thumb = this.root.querySelector(`.pdp-gallery__thumb[data-media-id="${mediaId}"]`);
    if (slide) {
      this.root.querySelectorAll('.pdp-gallery__main-slide').forEach((s) => s.classList.remove('is-active'));
      slide.classList.add('is-active');
    }
    if (thumb) {
      this.root.querySelectorAll('.pdp-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  }

  // ---------- Option selectors ----------
  bindOptionSelectors() {
    const buttons = this.root.querySelectorAll('.pdp-swatch, .pdp-pill');
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const optionIndex = btn.getAttribute('data-option-index');
        const optionValue = btn.getAttribute('data-option-value');
        this.selectedOptions[optionIndex] = optionValue;

        const row = btn.closest('.pdp-option-row');
        if (row) {
          row.querySelectorAll('.pdp-swatch, .pdp-pill').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
        }

        this.updateVariant();
      });
    });
  }

  updateVariant() {
    const selectedArr = Object.keys(this.selectedOptions)
      .sort((a, b) => a - b)
      .map((k) => this.selectedOptions[k]);

    const match = this.variants.find((v) =>
      v.options.every((opt, i) => opt === selectedArr[i])
    );

    if (!match) return;
    this.currentVariant = match;

    this.updatePriceDisplay();

    // Stock
    if (this.stockEl) {
      if (match.available) {
        this.stockEl.innerHTML = match.inventory_management && match.inventory_quantity > 0
          ? `<span class="pdp-stock-badge--in">✓ ${match.inventory_quantity} in stock</span>`
          : `<span class="pdp-stock-badge--in">✓ In stock</span>`;
      } else {
        this.stockEl.innerHTML = `<span class="pdp-stock-badge--out">Sold out</span>`;
      }
    }

    // SKU
    if (this.skuEl) {
      this.skuEl.innerHTML = `<strong>SKU:</strong> ${match.sku || '—'}`;
    }

    // Buttons
    const disabled = !match.available;
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = disabled;
      this.addToCartBtn.textContent = disabled ? 'Sold Out' : (this.addToCartBtn.dataset.originalLabel || this.addToCartBtn.textContent);
    }
    if (this.buyNowBtn) this.buyNowBtn.disabled = disabled;

    // Media sync
    if (match.featured_media && match.featured_media.id) {
      this.showMediaById(match.featured_media.id);
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const hasCents = Math.round(cents) % 100 !== 0;
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    });
    return `${formatted} Tk`;
  }

  // ---------- Quantity ----------
  getQuantity() {
    if (!this.qtyInput) return 1;
    const parsed = parseInt(this.qtyInput.value || '1', 10);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }

  /*
   * Renders the price for the current variant multiplied by the selected
   * quantity, so the shopper sees the actual line total. The discount
   * percentage is unaffected by quantity, so it's computed from unit prices.
   */
  updatePriceDisplay() {
    const variant = this.currentVariant;
    if (!variant) return;

    const qty = this.getQuantity();
    const lineTotal = variant.price * qty;

    if (this.priceCurrent) this.priceCurrent.textContent = this.formatMoney(lineTotal);
    if (this.stickyPriceEl) this.stickyPriceEl.textContent = this.formatMoney(lineTotal);

    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (this.priceCompare) {
      if (hasDiscount) {
        this.priceCompare.textContent = this.formatMoney(variant.compare_at_price * qty);
        this.priceCompare.style.display = '';
      } else {
        this.priceCompare.style.display = 'none';
      }
    }

    if (this.discountBadge) {
      if (hasDiscount) {
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        this.discountBadge.textContent = `-${pct}%`;
        this.discountBadge.style.display = '';
      } else {
        this.discountBadge.style.display = 'none';
      }
    }
  }

  bindQuantity() {
    const minus = this.root.querySelector('[data-qty-minus]');
    const plus = this.root.querySelector('[data-qty-plus]');

    if (minus && this.qtyInput) {
      minus.addEventListener('click', () => {
        this.qtyInput.value = Math.max(1, this.getQuantity() - 1);
        this.updatePriceDisplay();
      });
    }

    if (plus && this.qtyInput) {
      plus.addEventListener('click', () => {
        this.qtyInput.value = this.getQuantity() + 1;
        this.updatePriceDisplay();
      });
    }

    // Also handle direct typing into the quantity field
    if (this.qtyInput) {
      this.qtyInput.addEventListener('input', () => this.updatePriceDisplay());
      this.qtyInput.addEventListener('change', () => {
        this.qtyInput.value = this.getQuantity();
        this.updatePriceDisplay();
      });
    }
  }

  // ---------- Cart actions ----------
  bindCartActions() {
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.originalLabel = this.addToCartBtn.textContent;
      this.addToCartBtn.addEventListener('click', () => this.addToCart(false));
    }
    if (this.buyNowBtn) {
      this.buyNowBtn.addEventListener('click', () => this.addToCart(true));
    }
  }

  addToCart(redirectToCheckout) {
    if (!this.currentVariant) return;
    const qty = this.getQuantity();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: this.currentVariant.id, quantity: qty })
    })
      .then((res) => res.json())
      .then(() => {
        if (redirectToCheckout) {
          window.location.href = '/checkout';
        } else {
          document.dispatchEvent(new CustomEvent('cart:updated'));
          window.location.href = '/cart';
        }
      })
      .catch((err) => console.error('Add to cart failed:', err));
  }

  // ---------- Share ----------
  bindShare() {
    const copyBtn = this.root.querySelector('[data-copy-link]');
    if (!copyBtn) return;
    const labelSpan = copyBtn.querySelector('span');
    copyBtn.addEventListener('click', () => {
      const url = copyBtn.getAttribute('data-url');
      navigator.clipboard.writeText(url).then(() => {
        const original = labelSpan ? labelSpan.textContent : copyBtn.textContent;
        if (labelSpan) {
          labelSpan.textContent = 'Copied!';
        } else {
          copyBtn.textContent = '✓ Copied';
        }
        copyBtn.classList.add('is-copied');
        setTimeout(() => {
          if (labelSpan) {
            labelSpan.textContent = original;
          } else {
            copyBtn.textContent = original;
          }
          copyBtn.classList.remove('is-copied');
        }, 2000);
      });
    });
  }

  // ---------- Sticky mobile bar ----------
  bindStickyBar() {
    this.stickyBar = document.querySelector('[data-sticky-bar]');
    if (!this.stickyBar) return;

    this.stickyPriceEl = this.stickyBar.querySelector('[data-sticky-price]');
    const stickyAddBtn = this.stickyBar.querySelector('[data-sticky-add-to-cart]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', () => this.addToCart(false));
    }

    const actionsEl = this.root.querySelector('.pdp-actions');
    if (!actionsEl || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show the bar once the main action buttons have scrolled above the viewport
          const scrolledPast = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
          this.stickyBar.classList.toggle('is-visible', scrolledPast);
        });
      },
      { threshold: 0 }
    );
    observer.observe(actionsEl);
  }
}

window.PDPMainProduct = PDPMainProduct;
